// Load CSS files (order matters)

// Load `vuetify` styles
import 'vuetify/styles';

// Vuetify 4 → v3 compatibility shims (grid layout, CSS reset, etc.)
import '@/shared/styles/vuetify-v3-compat.css';
// Load Themes
import '../.helix/themes.css';

// Make sure to install FontAwesome icons after our helix themes css
import '@fortawesome/fontawesome-free/css/all.css';
// Self-hosted MDI (same package Vuetify's mdi iconset expects) — no third-party CDN
import '@mdi/font/css/materialdesignicons.css';

// Load default `helix-components` styles
import '@onify/helix-components/dist/themes/default.css';
import '@onify/helix-pages/dist/themes/default.css';

// Load app styles
import '@/shared/styles/main.scss';

// Load all css/scss files from `custom` directory. Note: omly auto import css with `.import` appended
import.meta.glob('./custom/styles/**/*.import.{css, scss}', { eager: true });

import './config/client-code-styles.js';
import { customerItemCardModules, genericItemCardModules } from './config/item-card-modules.js';
import Main from './main.vue';

// Vuetify icons
import { aliases, mdi } from 'vuetify/iconsets/mdi';
import { fa } from 'vuetify/iconsets/fa';

import { createPinia } from 'pinia';
import { createApp } from 'vue';

import { createVuetify } from 'vuetify';
import { sv, fi } from 'vuetify/locale';

import { getHelixConfig } from '@/config/helix.config';
import { createHead } from '@unhead/vue/client';

import { applyHelixLoginRedirectConfig, helixHttpRequestClient } from './plugins/http-client';
import {
  bootstrapPublicSettings,
  publicSettings,
  applyPublicCustomStyles,
  bootstrapPublicStrings,
  configureNestedModuleSettingKeys,
  configureLegacyCustomerConfig,
  configureItemCardModules,
  translations,
  BOOT_TEST_GLOBALS,
  bindVuetifyLocale,
  useHLocale,
} from '@onify/helix-core';
import { MODULE_REGISTRY } from '@onify/helix-core/citizen-runtime';
import { router } from './router';
import { themes as helixThemes } from '../.helix/themes';
import { helixSettings } from '../.helix/settings';
import layouts from '../.helix/layouts';
// Backward-compat only — see legacy-customer-config.ts. New customers have no
// entry here and are 100% API-driven.
import { LEGACY_CUSTOMER_CONFIG } from '../.helix/legacy-customer-config';

import VTooltip from 'v-tooltip';

configureNestedModuleSettingKeys(MODULE_REGISTRY.map((module) => module.id));
configureLegacyCustomerConfig(LEGACY_CUSTOMER_CONFIG);
configureItemCardModules(genericItemCardModules, customerItemCardModules);

// Public bootstrap always runs anonymously before mount.
// Do not gate on session — auth load continues via useSettingsLoadWhenAuthenticated.
const bootstrappedPublicSettings = await bootstrapPublicSettings(import.meta.env.HELIX_API_URL);
await applyPublicCustomStyles(import.meta.env.HELIX_API_URL, bootstrappedPublicSettings);
await bootstrapPublicStrings(import.meta.env.HELIX_API_URL, {
  publicSettings: bootstrappedPublicSettings,
  fallbackLocale: helixSettings.fallback_locale,
});

const effectiveHelixConfig = getHelixConfig(publicSettings);
applyHelixLoginRedirectConfig(effectiveHelixConfig);

// First-paint default — Vuetify init is synchronous. Prefer Hub public app_theme /
// default_theme when the pack is bundled; otherwise the generic YAML default.
// Runtime still re-resolves via the watcher in main.vue once authenticated settings arrive.
const publicBootTheme = bootstrappedPublicSettings?.app_theme ?? bootstrappedPublicSettings?.default_theme;
const DEFAULT_THEME_NAME =
  typeof publicBootTheme === 'string' && Object.hasOwn(helixThemes ?? {}, publicBootTheme) ? publicBootTheme : helixSettings.default_theme;
const { themes } = loadThemePacks(helixThemes);

const themePackProvider = hCreateThemePack(helixThemes, DEFAULT_THEME_NAME);

const vuetify = createVuetify({
  icons: {
    defaultSet: helixSettings.default_iconset ?? 'fa',
    aliases,
    sets: {
      mdi,
      fa,
    },
  },
  locale: {
    locale: helixSettings.fallback_locale ?? Locale.EN,
    fallback: helixSettings.fallback_locale,
    messages: { sv, fi },
  },
  theme: {
    variations: {
      colors: ['primary', 'secondary', 'surface-variant'],
      lighten: 2,
      darken: 2,
    },
    themes,
    // Use Default Theme name
    defaultTheme: themePackProvider.hGetThemePack(DEFAULT_THEME_NAME).name,
  },
  // Restore v3 breakpoint thresholds (Vuetify 4 defaults are smaller)
  display: {
    thresholds: {
      md: 960,
      lg: 1280,
      xl: 1920,
      xxl: 2560,
    },
  },
  defaults: {
    // Restore v3 uppercase button text (Vuetify 4 changed default to none).
    // text-uppercase lives in @layer vuetify-utilities which is above
    // vuetify-components, so utility classes like text-none still override it.
    VBtn: {
      class: 'text-uppercase',
    },
  },
});

// Single owner for `<html lang>` and Vuetify strings is hSetLocale. Do not pass
// `:locale` on v-locale-provider — that pins the subtree and ignores this root
// binding.
if (vuetify.locale?.current) {
  bindVuetifyLocale(vuetify.locale);
}

// Install Pinia for State Management
const pinia = createPinia();

// In test mode, allow tests to inject settings via window.__helixTestSettings
// before the Vue app starts (using page.addInitScript in Playwright tests).
// This enables reliable mocking of useOSettings() without relying on the
// async API→IndexedDB→Vue reactive chain.
const nextSettings =
  import.meta.env.MODE === 'test' && typeof window !== 'undefined' && window[BOOT_TEST_GLOBALS.HELIX_TEST_SETTINGS]
    ? { ...helixSettings, ...window[BOOT_TEST_GLOBALS.HELIX_TEST_SETTINGS] }
    : helixSettings;

const app = createApp(Main);

app
  .use(hInstallHelixCore(effectiveHelixConfig, themePackProvider, nextSettings, helixHttpRequestClient, layouts))
  .use(vuetify)
  .use(hCreateComponents())
  // Use Pinia
  .use(pinia)
  .use(router)
  .use(oCreateTranslatePlugin(pinia, nextSettings.fallback_locale))
  .use(createHead())
  .use(VTooltip);

app.mount('#app');

// Expose translation helpers for E2E tests only (dev:test runs with --mode test)
if (import.meta.env.MODE === 'test') {
  window[BOOT_TEST_GLOBALS.HELIX_ROUTER] = router;
  window[BOOT_TEST_GLOBALS.HELIX_LAYOUTS] = layouts;
  window[BOOT_TEST_GLOBALS.HELIX_LOCALE] = useHLocale().locale.value || nextSettings.fallback_locale || 'en';
  Object.defineProperty(window, BOOT_TEST_GLOBALS.HELIX_VUETIFY_LOCALE, {
    configurable: true,
    enumerable: true,
    get() {
      const current = vuetify.locale?.current;
      if (typeof current === 'string') {
        return current;
      }
      if (current && typeof current === 'object' && 'value' in current) {
        return current.value;
      }
      return undefined;
    },
  });
  window[BOOT_TEST_GLOBALS.HELIX_TRANSLATE] = (key, locale) => {
    const loc = locale || nextSettings.fallback_locale || 'en';
    return translations[loc]?.[key] ?? key;
  };
  window[BOOT_TEST_GLOBALS.HELIX_TRANSLATIONS] = translations;
}

// Global error handling
// app.config.errorHandler = (error) => {
//   console.log('error', error);
// };

export { app };
